import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Activity, AlertTriangle, Clock, Users, BarChart3, ChevronRight,
  Loader2, Shield, Eye, Zap
} from 'lucide-react';

const RISK_COLORS = { high: '#C85C5C', medium: '#D4A843', healthy: '#4E7A6B' };

export default function EngagementDashboard() {
  const [overview, setOverview] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineDate, setTimelineDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/engagement/admin/overview`).then(r => setOverview(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedSub) return;
    axios.get(`${API}/engagement/admin/subscriber/${selectedSub}`).then(r => setDetail(r.data)).catch(() => {});
    axios.get(`${API}/engagement/admin/timeline/${selectedSub}?date=${timelineDate}`).then(r => setTimeline(r.data)).catch(() => {});
  }, [selectedSub, timelineDate]);

  const formatDuration = (s) => {
    if (!s) return '0s';
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${Math.round(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" data-testid="engagement-dashboard">
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Care Intelligence</h1>
          <p className="text-xs text-adm-tertiary mt-1">Subscriber engagement & usage tracking</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div>
        ) : (
          <>
            {/* Subscriber Table */}
            <div className="rounded-xl border border-white/[0.06] overflow-hidden" style={{ backgroundColor: '#161B22' }} data-testid="engagement-table">
              <div className="grid grid-cols-8 gap-2 px-4 py-2.5 text-[9px] uppercase tracking-wider text-adm-tertiary border-b border-white/[0.04]">
                <span className="col-span-2">Subscriber</span>
                <span>Last Seen</span>
                <span>Sessions</span>
                <span>Avg Duration</span>
                <span>Protocol</span>
                <span>Daily Flow</span>
                <span>Risk</span>
              </div>
              {overview?.subscribers?.map(sub => {
                const riskColor = RISK_COLORS[sub.risk_status] || '#8A8681';
                return (
                  <div key={sub.subscriber_id} onClick={() => setSelectedSub(sub.subscriber_id)}
                    className={`grid grid-cols-8 gap-2 px-4 py-3 cursor-pointer transition-colors hover:bg-white/[0.02] border-b border-white/[0.03] ${selectedSub === sub.subscriber_id ? 'bg-white/[0.04]' : ''}`}
                    data-testid={`eng-row-${sub.subscriber_id}`}>
                    <span className="text-sm text-adm-primary col-span-2 truncate">{sub.name}</span>
                    <span className="text-xs text-adm-tertiary">{sub.last_active ? new Date(sub.last_active).toLocaleDateString() : 'Never'}</span>
                    <span className="text-xs text-adm-secondary">{sub.sessions_7d}</span>
                    <span className="text-xs text-adm-secondary">{formatDuration(sub.avg_duration)}</span>
                    <span className="text-xs text-adm-secondary">{sub.protocol_engagement}</span>
                    <span className="text-xs text-adm-secondary">{sub.daily_flow_usage}</span>
                    <Badge variant="outline" className="text-[9px] w-fit" style={{ borderColor: riskColor + '30', color: riskColor }}>
                      {sub.risk_status}
                    </Badge>
                  </div>
                );
              })}
              {(!overview?.subscribers || overview.subscribers.length === 0) && (
                <div className="p-8 text-center text-adm-tertiary text-sm">No engagement data yet</div>
              )}
            </div>

            {/* Detail view */}
            {selectedSub && detail && (
              <div className="grid grid-cols-2 gap-4">
                {/* Metrics */}
                <Card className="bg-adm-panel border-white/[0.06]">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-[10px] uppercase tracking-wider text-adm-tertiary">Engagement Metrics (7d)</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Sessions', value: detail.total_sessions_7d, icon: Activity },
                        { label: 'Active Days', value: `${detail.active_days_per_week}/7`, icon: Clock },
                        { label: 'Avg Duration', value: formatDuration(detail.avg_session_duration), icon: Zap },
                        { label: 'Total Time', value: formatDuration(detail.total_duration_7d), icon: BarChart3 },
                      ].map(m => (
                        <div key={m.label} className="p-2.5 rounded-lg bg-white/[0.02]">
                          <m.icon className="w-3 h-3 text-adm-tertiary mb-1" strokeWidth={1.5} />
                          <p className="text-lg font-['Outfit'] font-light text-adm-primary">{m.value}</p>
                          <p className="text-[9px] text-adm-tertiary">{m.label}</p>
                        </div>
                      ))}
                    </div>
                    {/* Page usage */}
                    {detail.page_usage && Object.keys(detail.page_usage).length > 0 && (
                      <div>
                        <p className="text-[9px] text-adm-tertiary uppercase tracking-wider mb-2">Section Usage</p>
                        {Object.entries(detail.page_usage).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([page, count]) => (
                          <div key={page} className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] text-adm-secondary w-20 truncate capitalize">{page.replace('-', ' ')}</span>
                            <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                              <div className="h-full bg-adm-accent/40 rounded-full" style={{ width: `${Math.min((count / Math.max(...Object.values(detail.page_usage))) * 100, 100)}%` }} />
                            </div>
                            <span className="text-[9px] text-adm-tertiary font-mono w-6 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Risks */}
                    {detail.risks?.length > 0 && (
                      <div>
                        <p className="text-[9px] text-adm-tertiary uppercase tracking-wider mb-2">Risk Alerts</p>
                        {detail.risks.map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs py-1">
                            <AlertTriangle className="w-3 h-3" style={{ color: RISK_COLORS[r.severity] }} />
                            <span className="text-adm-secondary">{r.detail}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Timeline */}
                <Card className="bg-adm-panel border-white/[0.06]">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-wider text-adm-tertiary">Activity Timeline</p>
                      <Input type="date" value={timelineDate} onChange={e => setTimelineDate(e.target.value)}
                        className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs w-32" />
                    </div>
                    <div className="max-h-80 overflow-y-auto space-y-1" data-testid="timeline-view">
                      {timeline?.timeline?.length ? timeline.timeline.map((t, i) => (
                        <div key={i} className="flex items-start gap-3 py-1.5">
                          <span className="text-[10px] text-adm-tertiary font-mono w-14 flex-shrink-0">
                            {t.time ? new Date(t.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                          <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                            t.type === 'session_start' ? 'bg-emerald-400' :
                            t.type === 'session_end' ? 'bg-rose-400' :
                            'bg-adm-accent/40'
                          }`} />
                          <span className="text-xs text-adm-secondary">{t.detail}</span>
                        </div>
                      )) : (
                        <p className="text-xs text-adm-tertiary text-center py-6">No activity recorded for this date</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
